import { api } from '@/api/api-client';

/**
 * Creates a consistent room name for a chat between a client and a filmmaker
 * Format: chat_client_{clientId}_filmmaker_{filmmakerId}
 * This ensures we're using entity IDs rather than auth user IDs
 */
export function createChatRoomName(
  clientId: string,
  filmmakerId: string
): string {
  // Always put client first, filmmaker second for consistency
  return `chat_client_${clientId}_filmmaker_${filmmakerId}`;
}

/**
 * Extracts client and filmmaker IDs from a chat room name
 * Returns an object with both IDs
 */
export function extractIdsFromRoomName(roomName: string): {
  clientId: string | null;
  filmmakerId: string | null;
} {
  try {
    // Match the room pattern: chat_client_{clientId}_filmmaker_{filmmakerId}
    const regex = /chat_client_([^_]+)_filmmaker_([^_]+)/;
    const match = roomName.match(regex);

    if (!match || match.length < 3) {
      console.warn(`Invalid room name format: ${roomName}`);
      return { clientId: null, filmmakerId: null };
    }

    return {
      clientId: match[1],
      filmmakerId: match[2],
    };
  } catch (error) {
    console.error('Error extracting IDs from room name:', error);
    return { clientId: null, filmmakerId: null };
  }
}

/**
 * Gets the other participant's ID based on current user's role and ID
 */
export function getOtherParticipantId(
  roomName: string,
  currentId: string,
  userRole: 'client' | 'filmmaker'
): string | null {
  const { clientId, filmmakerId } = extractIdsFromRoomName(roomName);

  if (!clientId || !filmmakerId) return null;

  if (userRole === 'client' && currentId === clientId) {
    return filmmakerId;
  } else if (userRole === 'filmmaker' && currentId === filmmakerId) {
    return clientId;
  }

  // If the current user is not part of this chat
  return null;
}

/**
 * Fetches all chat rooms for a specific user based on their role and ID
 */
export async function fetchUserChatRooms(
  userId: string,
  userRole: 'client' | 'filmmaker'
) {
  try {
    // Use messages edge function with search parameter
    const searchParam = userRole === 'client' 
      ? `client_${userId}` 
      : `filmmaker_${userId}`;
    
    const response = await api<any[]>(`/messages?room_name_contains=${searchParam}`);
    
    // Extract unique room names
    const uniqueRoomNames = [...new Set(response.map((item) => item.room_name))];
    return uniqueRoomNames;
  } catch (error) {
    console.error('Error fetching user chat rooms:', error);
    return [];
  }
}

/**
 * Fetches the latest message for a room
 */
export async function fetchLatestRoomMessage(roomName: string) {
  try {
    const response = await api<any[]>(`/messages?room_name=${roomName}&limit=1&order=desc`);
    return response[0] || null;
  } catch (error) {
    console.error(`Error fetching latest message for room ${roomName}:`, error);
    return null;
  }
}

/**
 * Fetches a user's name based on entity ID and role
 */
export async function fetchUserName(
  entityId: string,
  role: 'client' | 'filmmaker'
): Promise<string> {
  try {
    if (role === 'filmmaker') {
      // Use filmmakers edge function with path parameter
      const filmmaker = await api<any>(`/filmmakers/${entityId}`);
      
      if (filmmaker) {
        return filmmaker.first_name && filmmaker.last_name
          ? `${filmmaker.first_name} ${filmmaker.last_name}`
          : `Filmmaker ${entityId.substring(0, 8)}`;
      }
    } else {
      // Use clients edge function with path parameter
      const client = await api<any>(`/clients/${entityId}`);
      
      if (client) {
        return client.company_name || `Client ${entityId.substring(0, 8)}`;
      }
    }

    // Fallback
    return `${
      role === 'filmmaker' ? 'Filmmaker' : 'Client'
    } ${entityId.substring(0, 8)}`;
  } catch (error) {
    console.error('Error fetching user name:', error);
    return `${
      role === 'filmmaker' ? 'Filmmaker' : 'Client'
    } ${entityId.substring(0, 8)}`;
  }
}

/**
 * Fetches a user's avatar and name based on entity ID and role
 */
export async function fetchUserDetails(
  entityId: string,
  role: 'client' | 'filmmaker'
): Promise<{ name: string; avatar?: string }> {
  try {
    if (role === 'filmmaker') {
      // Use filmmakers edge function with path parameter
      const filmmaker = await api<any>(`/filmmakers/${entityId}`);
      
      if (filmmaker) {
        return {
          name:
            filmmaker.first_name && filmmaker.last_name
              ? `${filmmaker.first_name} ${filmmaker.last_name}`
              : `Filmmaker ${entityId.substring(0, 8)}`,
          avatar: filmmaker.profile_pic || undefined,
        };
      }
    } else {
      // Use clients edge function with path parameter
      const client = await api<any>(`/clients/${entityId}`);
      
      if (client) {
        return {
          name: client.company_name || `Client ${entityId.substring(0, 8)}`,
          avatar: client.logo_url || undefined,
        };
      }
    }

    // Fallback
    return {
      name: `${
        role === 'filmmaker' ? 'Filmmaker' : 'Client'
      } ${entityId.substring(0, 8)}`,
    };
  } catch (error) {
    console.error('Error fetching user details:', error);
    return {
      name: `${
        role === 'filmmaker' ? 'Filmmaker' : 'Client'
      } ${entityId.substring(0, 8)}`,
    };
  }
}

/**
 * Maps a user ID to the corresponding entity ID (client_id or filmmaker_id)
 */
export async function getUserEntityId(
  userId: string,
  role: 'client' | 'filmmaker' | 'admin'
): Promise<string | null> {
  try {
    if (role === 'filmmaker') {
      const response = await api<any[]>(`/filmmakers?user_id=${userId}`);
      return response[0]?.filmmaker_id || null;
    } else if (role === 'client') {
      const response = await api<any[]>(`/clients?user_id=${userId}`);
      return response[0]?.client_id || null;
    } else if (role === 'admin') {
      // For admin, try to get their client_id (admin users typically have a client account)
      const response = await api<any[]>(`/clients?user_id=${userId}`);
      return response[0]?.client_id || null;
    }
    return null;
  } catch (error) {
    console.error(`Error getting ${role} entity ID for user ${userId}:`, error);
    return null;
  }
}